// Tools
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
// Styles
import "./history.scss";
// Components
import { HistoryConversationList } from "./components/history-conversation-list/HistoryConversationList";
import { CircleLoader } from "../../../../../../../../shared/components/circle-loader/CircleLoader";
import { EmptyList } from "../../../../../../../../shared/components/empty-list/EmptyList";
import { HistoryHeader } from "./components/history-header/HistoryHeader";
import { NotificationSoundToggle } from "./components/notification-sound-toggle/NotificationSoundToggle";
import { CompactButton } from "../../../../../../../../shared/components/buttons/compact-button/CompactButton";
import { DotsLoader } from "../../../../../../../../shared/components/dots-loader/DotsLoader";
// Service
import { useChatSessions } from "./service/history.service";
import { useBumblebeeSessions } from "./service/bumblebee-history.service";
import { fetchBumblebeeSessionData } from "./service/load-bumblebee-session.service";
import { markBumblebeeChatReadService } from "./service/mark-bumblebee-read.service";
import { shouldMarkChatRead } from "../../../../../../../../shared/utils/should-mark-chat-read";
// Utils
import { hideModuleThunk } from "../../../../utils/module-utils/hide-module";
import {
	buildHistoryConversations,
	filterConversationsBySearch,
} from "./utils/build-history-conversations";
import {
	filterConversationsByToday,
	filterConversationsByYesterday,
	filterConversationsBySevenDays,
	filterConversationsByOlderThatSevenDays,
} from "./utils/filter-conversations-by-date";
import {
	DRAFTS_CHANGED_EVENT,
	getDraftMessageText,
} from "../dialog/utils/draft-sessions-storage";
import {
	hasInputDraft,
	INPUT_DRAFT_CHANGED_EVENT,
} from "../dialog/utils/setInputDraft";
// Types
import type { RootState, AppDispatch } from "../../../../../../../../shared/store/store";
import type { User } from "../../../../../../../../shared/types/user.type";
import type { SessionType } from "../../../../../../../../shared/types/session.type";
import { mapBumblebeeToSessionMetadata } from "../../../../../../../../shared/utils/bumblebeeSession";
import { SESSION_TYPE_BUMBLEBEE_STATUS } from "../../../../../../../../shared/types/bumblebee.type";
// Actions
import {
	setSessionId,
	setActiveDraftId,
	setActiveChatKind,
	setSessionData,
	setIsLoading,
	setFreezeUI,
	setSessionProgress,
} from "../../../../../../../../shared/store/slices/session.slice";
// Socket
import { sessionService } from "../../../../../../../../shared/websocket/services/session.service";
import { syncFreezeTextLoaderAnimation } from "../form/utils/freeze-animations";

export const History = () => {
	// ---------------- Dispatch
	const dispatch = useDispatch<AppDispatch>();
	const queryClient = useQueryClient();

	// ---------------- Refs
	const historyRef = useRef<HTMLDivElement | null>(null);
	const historyHeaderWrapRef = useRef<HTMLDivElement | null>(null);
	const textLoaderRef = useRef<HTMLDivElement | null>(null);
	// Индикатор монтирования компонента
	const freezeUiWasActiveRef = useRef(false);
	// Перший рендер із закритою шапкою пошуку — без «анімації закриття» від видимого стану
	const skipInitialHeaderHideAnimRef = useRef(true);
	// Одноразова ініціалізація сесії після першого завантаження списку (не скидати при refetch)
	const initialSessionBootstrapDoneRef = useRef(false);

	// ---------------- States
	// Local
	const [headerVisibleStatus, setHeaderVisibleStatus] =
		useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>("");
	// Global
	const userData: User | null = useSelector(
		(state: RootState) => state.user ?? null,
	);
	const sessionId: string | null = useSelector(
		(state: RootState) => state.session.sessionId,
	);
	const activeDraftId: string | null = useSelector(
		(state: RootState) => state.session.activeDraftId,
	);
	const sessionData = useSelector(
		(state: RootState) => state.session.sessionData,
	);
	const [draftsRevision, setDraftsRevision] = useState(0);
	const isSocketLoaded: boolean = useSelector(
		(state: RootState) => state.app.isSocketLoaded,
	);
	const isFreezeUI: boolean = useSelector(
		(state: RootState) => state.session.freezeUI,
	);
	const activeChatKind = useSelector(
		(state: RootState) => state.session.activeChatKind,
	);
	// Variables
	const dashedChat = "/img/icons/large-64/dashed-chat.png";
	const chatNotFound = "/img/icons/large-64/chat-not-found.png";

	// ---------------- Data
	const { data, isLoading } = useChatSessions(
		userData?.buyer_id != null ? String(userData.buyer_id) : undefined,
	);
	const { data: bumblebeeData, isLoading: isBumblebeeLoading } =
		useBumblebeeSessions(userData?.buyer_id != null);

	// ---------------- Functions
	const searchInputHandle = (value: string) => {
		setSearchValue(value);
	};
	const allConversations = useMemo(() => {
		void draftsRevision;
		const assistantSessions = data?.data?.sessions ?? [];
		const bumblebeeSessions = (bumblebeeData?.data?.sessions ?? []).map(
			mapBumblebeeToSessionMetadata,
		);
		return buildHistoryConversations([
			...assistantSessions,
			...bumblebeeSessions,
		]);
	}, [data?.data?.sessions, bumblebeeData?.data?.sessions, draftsRevision]);

	const getSearchResults = () => {
		if (searchValue.trim().length === 0) return [];
		return filterConversationsBySearch(allConversations, searchValue);
	};

	const hasMessagesInCurrentChat =
		sessionId !== null && (sessionData?.messages?.length ?? 0) > 0;
	const hasSessionInputDraft =
		sessionId !== null && hasInputDraft(sessionId);
	const hasNullSessionInput =
		sessionId === null &&
		activeDraftId !== null &&
		getDraftMessageText(activeDraftId).trim().length > 0;
	const isNewChatDisabled =
		!isFreezeUI &&
		!hasMessagesInCurrentChat &&
		!hasSessionInputDraft &&
		!hasNullSessionInput;
	const hideOrShowHeader = (status: boolean) => {
		setHeaderVisibleStatus(status);
		// Если закрываем поиск, то обнуляем инпут
		if (status === false) setSearchValue("");
	};
	const handleSetSessionId = (
		payloadSessionId: string,
		chatKind: SessionType = "assistant",
	) => {
		if (sessionId !== payloadSessionId) {
			dispatch(setFreezeUI(false));
			dispatch(setSessionProgress(0));
			dispatch(setActiveChatKind(chatKind));
			dispatch(setSessionId(payloadSessionId));
		}
	};
	const resetSessionId = () => {
		dispatch(setActiveDraftId(null));
		dispatch(setSessionId(null));
		dispatch(setSessionData(null));
		dispatch(setIsLoading(false));
		dispatch(setFreezeUI(false));
		dispatch(setSessionProgress(0));
	};

	// ---------------- Effects
	// Прячем шапку с окном поиска при клике вне нее
	useEffect(() => {
		if (!headerVisibleStatus) return;
		const handleDocumentClick = (e: MouseEvent) => {
			const target = e.target as Node | null;
			if (!historyRef.current) return;
			if (target && !historyRef.current.contains(target)) {
				hideOrShowHeader(false);
			}
		};
		document.addEventListener("click", handleDocumentClick);
		return () => {
			document.removeEventListener("click", handleDocumentClick);
		};
	}, [headerVisibleStatus]);

	useEffect(() => {
		const onDraftsChanged = () => setDraftsRevision((n) => n + 1);
		window.addEventListener(DRAFTS_CHANGED_EVENT, onDraftsChanged);
		window.addEventListener(INPUT_DRAFT_CHANGED_EVENT, onDraftsChanged);
		return () => {
			window.removeEventListener(DRAFTS_CHANGED_EVENT, onDraftsChanged);
			window.removeEventListener(INPUT_DRAFT_CHANGED_EVENT, onDraftsChanged);
		};
	}, []);

	// Переключении сессии при смене id
	useEffect(() => {
		if (!sessionId) return;

		if (activeChatKind === SESSION_TYPE_BUMBLEBEE_STATUS) {
			let cancelled = false;
			dispatch(setIsLoading(true));
			fetchBumblebeeSessionData(sessionId)
				.then((nextSessionData) => {
					if (cancelled) return;
					dispatch(setSessionData(nextSessionData));
					dispatch(setIsLoading(false));
					if (!shouldMarkChatRead()) return;
					void markBumblebeeChatReadService(sessionId).then((ok) => {
						if (!ok || cancelled) return;
						queryClient.invalidateQueries({
							queryKey: ["bumblebee-sessions-data"],
						});
					});
				})
				.catch(() => {
					if (cancelled) return;
					dispatch(setSessionData(null));
					dispatch(setIsLoading(false));
				});
			return () => {
				cancelled = true;
			};
		}

		sessionService.switchSession(sessionId);
	}, [sessionId, activeChatKind, dispatch, queryClient]);

	// Оновлення повідомлень BumbleBee, поки чат відкритий
	useEffect(() => {
		if (activeChatKind !== SESSION_TYPE_BUMBLEBEE_STATUS || !sessionId) return;

		const pollMessages = () => {
			void fetchBumblebeeSessionData(sessionId).then((nextSessionData) => {
				if (!nextSessionData) return;
				dispatch(setSessionData(nextSessionData));
				if (!shouldMarkChatRead()) return;
				void markBumblebeeChatReadService(sessionId).then((ok) => {
					if (!ok) return;
					queryClient.invalidateQueries({
						queryKey: ["bumblebee-sessions-data"],
					});
				});
			});
		};

		const intervalId = window.setInterval(pollMessages, 30000);
		const onVisible = () => {
			if (shouldMarkChatRead()) pollMessages();
		};
		document.addEventListener("visibilitychange", onVisible);
		return () => {
			window.clearInterval(intervalId);
			document.removeEventListener("visibilitychange", onVisible);
		};
	}, [sessionId, activeChatKind, dispatch, queryClient]);

	// Після першого завантаження списку: новий чат (без автовибору останньої сесії)
	useEffect(() => {
		if (!isSocketLoaded) return;
		const sessions = data?.data?.sessions;
		if (sessions == null) return;
		if (initialSessionBootstrapDoneRef.current) return;

		initialSessionBootstrapDoneRef.current = true;
		// Юзер вже в чаті (вибрав або оптимістично створив) — wipe не легітимний:
		// скидання можливе лише з початкового null-стану
		if (sessionId !== null) return;

		dispatch(setSessionId(null));
		dispatch(setSessionData(null));
		dispatch(setIsLoading(false));
	}, [isSocketLoaded, data, dispatch, sessionId]);

	// Показуємо/ховаємо текстовий лоадер лише під час freezeUI (без показу при першому монті)
	useEffect(() => {
		syncFreezeTextLoaderAnimation(
			textLoaderRef.current,
			isFreezeUI,
			freezeUiWasActiveRef,
		);
	}, [isFreezeUI]);

	useLayoutEffect(() => {
		const el = historyHeaderWrapRef.current;
		if (!el) return;

		gsap.killTweensOf(el);

		const reduced =
			typeof window !== "undefined" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		if (reduced) {
			if (headerVisibleStatus) {
				gsap.set(el, {
					height: "auto",
					y: 0,
					opacity: 1,
					overflow: "visible",
					pointerEvents: "auto",
				});
			} else {
				gsap.set(el, {
					height: 0,
					y: 0,
					opacity: 0,
					overflow: "hidden",
					pointerEvents: "none",
				});
			}
			skipInitialHeaderHideAnimRef.current = false;
			return () => {
				gsap.killTweensOf(el);
			};
		}

		if (!headerVisibleStatus && skipInitialHeaderHideAnimRef.current) {
			skipInitialHeaderHideAnimRef.current = false;
			gsap.set(el, {
				height: 0,
				opacity: 0,
				scale: 1,
				overflow: "hidden",
				pointerEvents: "none",
				clearProps: "transform",
			});
			return () => {
				gsap.killTweensOf(el);
			};
		}
		skipInitialHeaderHideAnimRef.current = false;

		if (headerVisibleStatus) {
			gsap.fromTo(
				el,
				{ height: 0, opacity: 0 },
				{
					height: "26px",
					duration: 0.2,
					ease: "power2.inOut",
					onComplete: () => {
						gsap.set(el, {
							opacity: 0,
							overflow: "visible",
							pointerEvents: "auto",
							clearProps: "transform",
						});
						gsap.fromTo(
							el,
							{
								opacity: 0,
								scale: 0.98,
							},
							{
								scale: 1,
								opacity: 1,
								duration: 0.25,
								ease: "elastic.out(1.4, 1.7)",
								onComplete: () => {
									gsap.set(el, { pointerEvents: "auto" });
								},
							},
						);
					},
				},
			);
		} else {
			gsap.set(el, { pointerEvents: "none" });
			gsap.fromTo(
				el,
				{
					opacity: 1,
					scale: 1,
				},
				{
					opacity: 0,
					scale: 0.98,
					duration: 0.2,
					ease: "power2.inOut",
					onComplete: () => {
						gsap.fromTo(
							el,
							{
								height: "26px",
							},
							{
								height: 0,
								duration: 0.2,
								ease: "power2.out",
								onComplete: () => {
									gsap.set(el, { pointerEvents: "none" });
								},
							},
						);
					},
				},
			);
		}

		return () => {
			gsap.killTweensOf(el);
		};
	}, [headerVisibleStatus]);

	return (
		<div className='history window' ref={historyRef}>
			<div className='window__header'>
				<div className='window__header_left'>
					<img src='/img/icons/list.png' alt='chats history icon' />
					<div>Історія чатів</div>
				</div>
				<div className='window__header_center'>
					<NotificationSoundToggle disabled={isFreezeUI} />
					<CompactButton
						children={"Новий чат"}
						type={"button"}
						unactive={isNewChatDisabled}
						iconLeft={<img src='/img/icons/add.png' alt='report icon' />}
						className='window-header-button'
						theme={"default"}
						onClick={() => resetSessionId()}
					/>
					<div
						onClick={() => hideOrShowHeader(!headerVisibleStatus)}
						className={`
							history__search-button 
							${isFreezeUI ? "freeze-ui" : ""}
							${headerVisibleStatus ? "active" : ""}
						`}>
						{!headerVisibleStatus ? (
							<img src='/img/icons/search.png' alt='search icon' />
						) : (
							<img src='/img/icons/search-off.png' alt='search icon' />
						)}
					</div>
				</div>
				<div className='window__header_right'>
					<div
						className='window__header_right_close-button'
						role='button'
						aria-label='hide chat history'
						onClick={() => dispatch(hideModuleThunk("chat_history"))}>
						<img src='/img/icons/close.png' alt='close button' />
					</div>
				</div>
			</div>
			<div
				className={`window__body ${!headerVisibleStatus ? "no-vertical-paddings" : ""}`}>
				<div
					ref={historyHeaderWrapRef}
					className={`history__header-anim ${headerVisibleStatus ? "history__header-anim--open" : ""}`}>
					<HistoryHeader value={searchValue} actionInput={searchInputHandle} />
				</div>
				<div className='window__body_list without-padding-top'>
					<div
						className={`window__body_list-inner time-blocks-wrapper ${isFreezeUI ? "freeze-ui" : ""}`}>
						{(isLoading || isBumblebeeLoading) && (
							<div className='window__loader'>
								<CircleLoader />
							</div>
						)}
						<div
							ref={textLoaderRef}
							className='window__text-loader'
							style={{ opacity: 0, pointerEvents: "none" }}>
							<div className='window__text-loader_bg'></div>
							<DotsLoader />
							<div className='window__text-loader_text'>
								Будь ласка, зачекайте. Виконується запит
							</div>
						</div>
						{!isLoading && !isBumblebeeLoading && allConversations.length === 0 && (
							<div className='window__loader'>
								<EmptyList image={dashedChat} text={"Чати відсутні"} />
							</div>
						)}
						{
							// Если шапка показана и есть значение в инпуте, показываем результаты поиска
							headerVisibleStatus && searchValue.trim().length > 0 ? (
								(() => {
									const results = getSearchResults();
									if (results.length === 0) {
										return (
											<div className='time-block empty'>
												<div className='time-block__title'>
													Результати пошуку:
												</div>
												<div className='window__loader'>
													<EmptyList
														image={chatNotFound}
														text={"Чат не знайдено"}
													/>
												</div>
											</div>
										);
									}
									return (
										<div className='time-block'>
											<div className='time-block__title'>Результати пошуку</div>
											<div className='time-block__chats'>
												<HistoryConversationList
													items={results}
													sessionId={sessionId}
													activeDraftId={activeDraftId}
													onSetSessionId={handleSetSessionId}
												/>
											</div>
										</div>
									);
								})()
							) : (
								<>
									{filterConversationsByToday(allConversations).length > 0 && (
										<div className='time-block'>
											<div className='time-block__title'>Сьогодні</div>
											<div className='time-block__chats'>
												<HistoryConversationList
													items={filterConversationsByToday(allConversations)}
													sessionId={sessionId}
													activeDraftId={activeDraftId}
													onSetSessionId={handleSetSessionId}
												/>
											</div>
										</div>
									)}
									{filterConversationsByYesterday(allConversations).length >
										0 && (
										<div className='time-block'>
											<div className='time-block__title'>Вчора</div>
											<div className='time-block__chats'>
												<HistoryConversationList
													items={filterConversationsByYesterday(
														allConversations,
													)}
													sessionId={sessionId}
													activeDraftId={activeDraftId}
													onSetSessionId={handleSetSessionId}
												/>
											</div>
										</div>
									)}
									{filterConversationsBySevenDays(allConversations).length >
										0 && (
										<div className='time-block'>
											<div className='time-block__title'>Останні 7 днів</div>
											<div className='time-block__chats'>
												<HistoryConversationList
													items={filterConversationsBySevenDays(
														allConversations,
													)}
													sessionId={sessionId}
													activeDraftId={activeDraftId}
													onSetSessionId={handleSetSessionId}
												/>
											</div>
										</div>
									)}
									{filterConversationsByOlderThatSevenDays(allConversations)
										.length > 0 && (
										<div className='time-block'>
											<div className='time-block__title'>Старі</div>
											<div className='time-block__chats'>
												<HistoryConversationList
													items={filterConversationsByOlderThatSevenDays(
														allConversations,
													)}
													sessionId={sessionId}
													activeDraftId={activeDraftId}
													onSetSessionId={handleSetSessionId}
												/>
											</div>
										</div>
									)}
								</>
							)
						}
					</div>
				</div>
			</div>
		</div>
	);
};
